import React, {useMemo} from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  Tab,
  TabList,
  Tabs,
  Link as ChakraLink,
} from '@chakra-ui/react';
import {PageHeader} from '../PageHeader';
import {Navigate, Route, Routes, matchPath, useLocation} from 'react-router';
import {Link} from 'react-router-dom';
import {
  Code,
  List,
  SettingsAdjust,
  Share,
  TextAlignLeft,
  User,
  UserMultiple,
} from '@carbon/icons-react';
import {IntegrationDetails} from '../Integrations/IntegrationDetails';
import IntegrationsList from '../Integrations/Integrations';
import {SalesforceErrors} from '../Integrations/SalesforceErrors';
import {
  useFeatures,
  useIsFreemiumV3Customer,
  useIsFreeOrListBuilderCustomer,
} from '../../hooks/api/metadata';
import {Account} from './Account';
import {Users} from './Users';
import {useIsFreemiumCustomer} from '../../hooks/api/metadata';
import {Fields} from './Fields/Fields';
import {Signals} from './Signals';
import {Title} from '../Title';
import {TestLists} from './TestLists/TestLists';
import {PlanAndCredits} from './PlanAndCredits';
import {Credits} from '../Icons/Credits';
import Api from './Api';

export function useSettings() {
  const isFreemiumCustomer = useIsFreemiumCustomer();
  const isFreemiumV3Customer = useIsFreemiumV3Customer();
  const isFreeOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();

  return useMemo(() => {
    return {
      account: [
        {
          label: 'Account',
          path: 'account',
          element: <Account />,
          icon: <User />,
        },
      ],
      team: [
        {
          label: 'Plan & Credits',
          path: 'plan',
          element: <PlanAndCredits />,
          icon: <Credits />,
          hidden: false,
        },
        {
          label: 'Users',
          path: 'users',
          element: <Users />,
          icon: <UserMultiple />,
          hidden: false,
        },
        {
          label: 'Integrations',
          path: 'integrations',
          element: <IntegrationsList />,
          icon: <Share />,
          hidden: isFreemiumCustomer && !isFreemiumV3Customer,
        },
        {
          label: 'Fields',
          path: 'fields',
          element: <Fields />,
          icon: <List />,
          hidden: isFreeOrListBuilderCustomer,
        },
        {
          label: 'Signals',
          path: 'signals',
          element: <Signals />,
          icon: <SettingsAdjust />,
          hidden: isFreeOrListBuilderCustomer,
        },
        {
          label: 'Test Lists',
          path: 'testLists',
          element: <TestLists />,
          icon: <TextAlignLeft />,
          hidden: isFreeOrListBuilderCustomer,
        },
        {
          label: 'API',
          path: 'api',
          element: <Api />,
          icon: <Code />,
          hidden: false,
        },
      ]
        .filter(({hidden}) => !hidden)
        .map(({hidden: _hidden, ...rest}) => ({
          ...rest,
        })),
    };
  }, [isFreemiumCustomer, isFreeOrListBuilderCustomer, isFreemiumV3Customer]);
}

const Settings = () => {
  return (
    <Routes>
      <Route path="integrations/errors" element={<SalesforceErrors />} />
      <Route
        path="integrations/:integration"
        element={<IntegrationDetails />}
      />
      <Route path="/*" element={<AccountSettings />} />
    </Routes>
  );
};

const AccountSettings = () => {
  const location = useLocation();
  const {team, account} = useSettings();
  const tabGroups = [
    {
      section: 'Your Settings',
      tabs: account,
    },
    {
      section: 'Team Settings',
      tabs: team,
    },
  ];
  const selectedIndex = tabGroups
    .map((group) => group.tabs)
    .flat()
    .findIndex((tab) => matchPath(`/settings/${tab.path}`, location.pathname));

  const features = useFeatures();
  const hasAllFeatures = (['apiKey', 'hubspot', 'salesforce'] as const).every(
    (feature) => features.includes(feature)
  );

  return (
    <Box>
      <PageHeader title="Account & Settings" />
      {!hasAllFeatures && (
        <Box mx={4} mb={2}>
          <Alert borderRadius={4} status="warning">
            <AlertIcon />
            <Box>
              Looking for additional integrations and API access?
              <ChakraLink
                color="kblue.400"
                href="mailto:hello@keyplay.io?subject=New Integration Request"
                ms={2}
                target="_blank"
              >
                Contact us to learn more
              </ChakraLink>
              .
            </Box>
          </Alert>
        </Box>
      )}
      <Flex gap={8} maxW={'1200px'} my={8} mx="auto" px="2">
        <Tabs
          h="fit-content"
          index={selectedIndex}
          orientation="vertical"
          variant="unstyled"
        >
          <TabList as={Flex} gap="8" width="200px">
            {tabGroups.map(({section, tabs}) => {
              return (
                <Box key={section}>
                  <Box
                    fontSize="sm"
                    fontWeight="normal"
                    color="kgray.300"
                    textTransform="uppercase"
                    mb={4}
                  >
                    {section}
                  </Box>
                  {tabs.map((tab) => (
                    <Tab
                      as={Link}
                      color="kgray.300"
                      fontWeight="500"
                      to={tab.path}
                      key={tab.path}
                      justifyContent="start"
                      _hover={{
                        color: 'kgray.400',
                      }}
                      _selected={{
                        bgColor: 'kgray.100',
                        color: 'kgray.400',
                      }}
                      _focus={{
                        boxShadow:
                          'inset 3px 0 0 0 var(--chakra-colors-kblue-400)',
                        color: 'kgray.400',
                        outline: 'none',
                      }}
                    >
                      <Box mr={4}>{tab.icon}</Box>
                      {tab.label}
                    </Tab>
                  ))}
                </Box>
              );
            })}
          </TabList>
        </Tabs>

        <Box marginEnd={4} flex="1">
          <Routes>
            {tabGroups.map((group) => {
              return group.tabs.map((tab, index) => {
                return (
                  <Route
                    key={index}
                    path={tab.path}
                    element={<Title title={tab.label}>{tab.element}</Title>}
                  />
                );
              });
            })}
            <Route path="/*" element={<Navigate to="account" />} />
          </Routes>
        </Box>
      </Flex>
    </Box>
  );
};

export default Settings;
